import { useMemo } from "react";
import { useStateOfSalesV2Forecast, type ForecastTimePeriod } from "../state-of-sales-v2/useForecastData";
import { getStateOfSalesV2ForecastPosition } from "../state-of-sales-v2/forecastPosition";
import { canonicalForecastRows } from "./canonicalForecastRows";

/** Shared caller adapter; never replaces missing/unavailable V2 with a whole V1 forecast. */
export function useCanonicalForecast(period: ForecastTimePeriod, accumulated = false, pipeline = "all", owner = "all") {
  const filters = useMemo(() => ({
    period, accumulated, pipelines: pipeline === "all" ? [] : [pipeline],
    owners: owner === "all" ? [] : [owner], teams: [],
  }), [period, accumulated, pipeline, owner]);
  const model = useStateOfSalesV2Forecast({ syncUrl: false, view: "overview", controlledFilters: filters });
  const chartData = model.error || model.isLoading ? []
    : canonicalForecastRows(model.chartData, period === "prev_quarter", accumulated);
  const position = model.forecast
    ? getStateOfSalesV2ForecastPosition(model.forecast, period, accumulated, model.chartData)
    : null;
  const getDealsForMonths = (months: ReadonlySet<string>) => {
    const open = model.getForecastDealsForMonths(months)
      .filter(deal => Number(deal.forecast_contribution ?? 0) > 0);
    const won = model.filteredPreparedDeals.filter(deal => deal.is_won && deal.closed_at
      && months.has(`${new Date(deal.closed_at).getFullYear()}-${String(new Date(deal.closed_at).getMonth() + 1).padStart(2, "0")}`))
      .map(deal => ({ ...deal, forecast_contribution: Math.round(Number(deal.value)) }));
    return period === "prev_quarter" ? won : [...won, ...open];
  };
  return { ...model, chartData, position, getDealsForMonths };
}
